// src/app/services/device-fingerprint.service.ts
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DeviceFingerprintService {

  constructor() {}

  /**
   * Public: returns a SHA-256 hex fingerprint string.
   */
  async getFingerprint(): Promise<string> {
    const parts: string[] = [];

    // Basic navigator properties
    parts.push(`userAgent:${this.safe(() => navigator.userAgent)}`);
    parts.push(`platform:${this.safe(() => navigator.platform)}`);
    parts.push(`vendor:${this.safe(() => (navigator as any).vendor)}`);
    parts.push(`language:${this.safe(() => navigator.language)}`);
    parts.push(`languages:${this.safe(() => (navigator.languages || []).join(','))}`);

    // Hardware / capabilities
    parts.push(`hardwareConcurrency:${this.safe(() => (navigator as any).hardwareConcurrency)}`);
    parts.push(`deviceMemory:${this.safe(() => (navigator as any).deviceMemory)}`);
    parts.push(`maxTouchPoints:${this.safe(() => (navigator as any).maxTouchPoints)}`);

    // Screen
    parts.push(`screen:${this.safe(() => screen.width + 'x' + screen.height + 'x' + screen.colorDepth)}`);

    // Timezone / offset
    parts.push(`timezone:${this.safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone)}`);
    parts.push(`timezoneOffset:${this.safe(() => new Date().getTimezoneOffset())}`);

    // Plugins & mimeTypes (may be empty in modern browsers)
    try {
      if (navigator.plugins && navigator.plugins.length) {
        const pluginNames = Array.from(navigator.plugins).map(p => p.name + '::' + (p.description || '')).join(';');
        parts.push(`plugins:${pluginNames}`);
      } else {
        parts.push(`plugins:[]`);
      }
    } catch (e) {
      parts.push(`plugins:error`);
    }

    // Canvas fingerprint
    try {
      const canvasFP = await this.getCanvasFingerprint();
      parts.push(`canvas:${canvasFP}`);
    } catch (e) {
      parts.push('canvas:error');
    }

    // WebGL fingerprint
    try {
      const webglFP = this.getWebGLFingerprint();
      parts.push(`webgl:${webglFP}`);
    } catch (e) {
      parts.push('webgl:error');
    }

    // Audio fingerprint is possible but more intrusive; omitted by default.

    const raw = parts.join('||');
    const hash = await this.sha256Hex(raw);
    return hash;
  }

  // Safe accessor to avoid throwing if property absent
  private safe(fn: () => any): string {
    try {
      const v = fn();
      return v === undefined || v === null ? 'null' : String(v);
    } catch (e) {
      return 'err';
    }
  }

  // Canvas fingerprint: draw shapes/text and return data hash
  private async getCanvasFingerprint(): Promise<string> {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 200;
      canvas.height = 50;
      const ctx = canvas.getContext('2d');
      if (!ctx) return 'nocanvas';

      // Draw text with fallback fonts, shapes, and composite ops
      ctx.textBaseline = 'top';
      ctx.font = "16px 'Arial', 'Helvetica', sans-serif";
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillRect(0, 0, 200, 50);
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillText('Fingerprint test — 𝕯𝖊𝖛ïçê', 2, 2);
      ctx.fillStyle = 'rgba(120, 200, 80, 0.7)';
      ctx.fillRect(10, 20, 100, 20);

      // Add tiny stroke
      ctx.strokeStyle = 'rgba(10,10,10,0.2)';
      ctx.strokeRect(5.5, 5.5, 120, 30);

      const data = canvas.toDataURL();
      return await this.sha256Hex(data);
    } catch (e) {
      return 'canvas-exception';
    }
  }

  // WebGL renderer/vendor fingerprint
  private getWebGLFingerprint(): string {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || (canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);
      if (!gl) return 'nowebgl';

      const debugInfo = (gl.getExtension('WEBGL_debug_renderer_info') ||
                         gl.getExtension('MOZ_WEBGL_debug_renderer_info') ||
                         gl.getExtension('WEBKIT_WEBGL_debug_renderer_info')) as any;

      const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown';
      const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown_renderer';

      // Some additional checks: shading language, max texture size, etc.
      const params = [
        `vendor:${vendor}`,
        `renderer:${renderer}`,
        `shading:${gl.getParameter(gl.SHADING_LANGUAGE_VERSION)}`,
        `maxTex:${gl.getParameter(gl.MAX_TEXTURE_SIZE)}`,
        `maxVertexAttribs:${gl.getParameter(gl.MAX_VERTEX_ATTRIBS)}`,
      ].join('|');

      return params;
    } catch (e) {
      return 'webgl-exception';
    }
  }

  // Convert string to SHA-256 hex using SubtleCrypto
  private async sha256Hex(input: string): Promise<string> {
    const enc = new TextEncoder();
    const data = enc.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hex;
  }
}
